import React, { useEffect, useMemo, useState } from 'react';
import { Chain } from '../../types';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { apiGet, apiPost, apiDelete } from '../../utils/apiClient';

export default function ChainsPage() {
  const { theme } = useTheme();
  const [chains, setChains] = useState<Chain[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet('/api/chains');
      if (!res.ok) throw new Error(`Failed to load chains (${res.status})`);
      const data = await res.json();
      setChains(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load chains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return chains.filter(c =>
      c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
    );
  }, [chains, search]);

  const execute = async (id: string) => {
    if (!window.confirm('Execute this chain now?')) return;
    try {
      const res = await apiPost(`/api/chains/${id}/execute`);
      if (!res.ok) throw new Error(`Failed to start chain (${res.status})`);
      const data = await res.json();
      alert(`Chain run started: ${data.run_id}`);
    } catch (e: any) {
      alert(e?.message || 'Failed to execute chain');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this chain? This cannot be undone.')) return;
    try {
      const res = await apiDelete(`/api/chains/${id}`);
      if (!res.ok) throw new Error(`Failed to delete chain (${res.status})`);
      await load();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete chain');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, color: theme.text }}>Chains</h2>
          <p style={{ marginTop: 6, color: theme.textSecondary }}>Orchestrate pipelines with dependencies</p>
        </div>
        <button onClick={() => navigate('/chains/new')} style={{
          background: theme.buttonBackground, color: theme.buttonText, border: 'none', padding: '10px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer'
        }}>New Chain</button>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search chains..." style={{
          padding: '10px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: 8, width: 300, background: theme.inputBackground, color: theme.text
        }} />
        <button onClick={load} style={{ border: `1px solid ${theme.border}`, padding: '10px 14px', borderRadius: 8, background: theme.cardBackground, color: theme.text, cursor: 'pointer' }}>Refresh</button>
      </div>

      <div style={{ marginTop: 16, background: theme.cardBackground, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: theme.textSecondary }}>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Name</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Description</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Steps</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Created</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 16, color: theme.textSecondary }}>Loading...</td></tr>
            )}
            {error && !loading && (
              <tr><td colSpan={5} style={{ padding: 16, color: '#ef4444' }}>{error}</td></tr>
            )}
            {!loading && !error && filtered.map(c => (
              <tr key={c.chain_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <td style={{ padding: 12 }}>
                  <Link to={`/chains/${c.chain_id}`} style={{ fontWeight: 700, color: theme.text, textDecoration: 'none' }}>{c.name}</Link>
                </td>
                <td style={{ padding: 12, color: theme.textSecondary }}>{c.description || '-'}</td>
                <td style={{ padding: 12, color: theme.text }}>{c.steps?.length ?? 0}</td>
                <td style={{ padding: 12, color: theme.textSecondary }}>{new Date(c.created_at).toLocaleString()}</td>
                <td style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => execute(c.chain_id)} style={{ border: `1px solid ${theme.successDark}`, background: theme.successLight, color: theme.successDark, padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>Execute</button>
                  <Link to={`/chains/${c.chain_id}`} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, padding: '8px 10px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' }}>View</Link>
                  <button onClick={() => remove(c.chain_id)} style={{ border: `1px solid ${theme.errorDark}`, background: theme.errorLight, color: theme.errorDark, padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>Delete</button>
                </td>
              </tr>
            ))}
            {!loading && !error && filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 16, color: theme.textSecondary }}>No chains found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
